import { forwardRef, useEffect, useRef } from 'react'
import { InputCounterinputVariants } from './input-counter-input.styles'

import { useInputCounterContext } from '../../input-counter'
import type { InputCounterScopedProps } from '../../input-counter.types'

type InputCounterInputProps = Omit<React.ComponentProps<'input'>, 'value'> & {}

const MIN_VALUE_LIMIT = 0
const MAX_VALUE_LIMIT = 999

export const InputCounterInput = forwardRef<
  HTMLInputElement,
  InputCounterInputProps
>(({ ...props }, ref) => {
  const { __scopeInputCounter } =
    props as InputCounterScopedProps<InputCounterInputProps>

  const {
    currentValue,
    setCurrentValue,
    disabled,
    readOnly,
    hasError,
    onValueChange,
  } = useInputCounterContext('InputCounterInput', __scopeInputCounter)

  const inputRef = useRef<HTMLInputElement | null>(null)

  function ensureValueIsNumeric(value: string) {
    const regexForValidadeNumber = /[^0-9]/g
    const numbersEntered = value.replace(regexForValidadeNumber, '').trim()

    const _currentValue = Math.min(
      MAX_VALUE_LIMIT,
      Math.max(
        MIN_VALUE_LIMIT,
        Number.parseInt(numbersEntered) || MIN_VALUE_LIMIT
      )
    )

    setCurrentValue(Number(_currentValue))
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = currentValue > 0 ? String(currentValue) : ''
      onValueChange(currentValue)
    }
  }, [currentValue])

  return (
    <input
      {...props}
      ref={node => {
        if (typeof ref === 'function') {
          ref(node)
        }

        inputRef.current = node
      }}
      inputMode="numeric"
      type="tel"
      pattern="[0-9]*"
      className={InputCounterinputVariants({ hasError })}
      maxLength={3}
      onChange={event => {
        ensureValueIsNumeric(event.target.value)
        onValueChange(Number(event.target.value))
      }}
      value={currentValue}
      disabled={disabled}
      readOnly={readOnly}
      placeholder="0"
    />
  )
})

InputCounterInput.displayName = 'InputCounterInput'
